import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useEffect,
} from 'react';
import { userAxios, setupAxiosInterceptors } from '../userAxios';
import { UserContext } from './UserProvider';

export const AccountsContext = createContext();
const API_URL = process.env.REACT_APP_API_URL;

const AccountsProvider = ({ children }) => {
  const { token, handleInvalidToken } = useContext(UserContext);
  const [accounts, setAccounts] = useState([]);
  const [linkedAccounts, setLinkedAccounts] = useState([]);

  useEffect(() => {
    setupAxiosInterceptors(handleInvalidToken);
  }, [handleInvalidToken]);

  const getAccounts = useCallback(
    async (vendorId) => {
      try {
        const response = await userAxios.get(
          `${API_URL}/vendors/${vendorId}/accounts`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setAccounts(response.data);
      } catch (error) {
        console.error('Error fetching accounts:', error);
      }
    },
    [token]
  );

  const getLinkedAccounts = useCallback(
    async (vendorId) => {
      try {
        const response = await userAxios.get(
          `${API_URL}/vendors/${vendorId}/linked/accounts`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setLinkedAccounts(response.data);
      } catch (error) {
        console.error('Error fetching linked accounts:', error);
      }
    },
    [token]
  );

  const sendInvite = async (email, vendorId) => {
    try {
      await userAxios.post(
        `${API_URL}/portal/accounts`,
        {
          email,
          vendor: Number(vendorId),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    } catch (error) {
      if (error.response?.status === 409) {
        alert('Account Already Activated');
      } else {
        alert('Account failed to activate');
      }
    }
  };

  const createAccount = useCallback(
    async (accountData) => {
      const roleMapping = { Admin: 1, User: 2, Vendor: 3 };
      try {
        const dataToSend = { ...accountData };
        if (dataToSend.role && roleMapping[dataToSend.role]) {
          dataToSend.role = roleMapping[dataToSend.role];
        }
        const response = await userAxios.post(`${API_URL}/users`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAccounts((prev) => [...prev, response.data]);
        return response;
      } catch (error) {
        console.error('Error creating account:', error);
        throw error;
      }
    },
    [token]
  );

  const updateAccount = useCallback(
    async (accountId, accountData) => {
      try {
        const response = await userAxios.put(
          `${API_URL}/users/${accountId}`,
          accountData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setAccounts((prev) =>
          prev.map((acc) => (acc.id === accountId ? response.data : acc))
        );
      } catch (error) {
        console.error('Error updating account:', error);
      }
    },
    [token]
  );

  const updateAccounts = useCallback(
    async (accountData) => {
      try {
        await userAxios.put(`${API_URL}/users`, accountData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (error) {
        console.error('Error updating accounts:', error);
      }
    },
    [token]
  );

  const deleteAccount = useCallback(
    async (accountId) => {
      try {
        await userAxios.delete(`${API_URL}/users/${accountId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAccounts((prev) => prev.filter((acc) => acc.id !== accountId));
      } catch (error) {
        console.error('Error deleting account:', error);
      }
    },
    [token]
  );

  const handleImpersonate = useCallback(
    async (username) => {
      try {
        const { data } = await userAxios.get(
          `${API_URL}/users/${username}/impersonate`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        window.open(data, '_blank');
      } catch (error) {
        console.error('Failed to impersonate account:', error);
      }
    },
    [token]
  );

  const forgotPassword = async (username) => {
    try {
      await userAxios.post(`${API_URL}/forgotpassword`, { username });
    } catch (error) {
      console.error('Error sending forgot password request:', error);
    }
  };

  return (
    <AccountsContext.Provider
      value={{
        accounts,
        getAccounts,
        getLinkedAccounts,
        linkedAccounts,
        setLinkedAccounts,
        sendInvite,
        createAccount,
        updateAccount,
        updateAccounts,
        deleteAccount,
        handleImpersonate,
        forgotPassword,
      }}
    >
      {children}
    </AccountsContext.Provider>
  );
};

export default AccountsProvider;
